import { Buffer } from "buffer";

import moment from "moment-timezone";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

import "react-toastify/dist/ReactToastify.css";

import blogimg from "assets/images/blog-img.webp";
import favicon from "assets/images/indianic-short-logo.svg";
import logo from "assets/images/logo.svg";
import profilepic from "assets/images/profile-pic.jpg";
import apiConfig from "configs/apiConfig";
import generalConfig from "configs/generalConfig";
import { DEFAULT_DATE_FORMAT, DEFAULT_LANGUAGE_CODE } from "utils/constants";
import { localStorageKeys } from "utils/localStorageKeys";

/******************* 
@Purpose : Used for show message notification
@Parameter : text, type, autoClose, position
@Author : INIC
******************/
export const messageNotification = (
  text,
  type = "success",
  autoClose = generalConfig.TOAST_TIMER,
  position = "top-right"
) => {
  toast[type](text, {
    position: position,
    autoClose: autoClose,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });
};

/******************* 
@purpose : Used for encode data into base 64
@Parameter : {data} 
@Author : INIC
******************/
export const encodeBase64 = (data) => {
  return Buffer.from(data).toString("base64");
};

/******************* 
@purpose : Used for decode data from base 64
@Parameter : {data} 
@Author : INIC
******************/
export const decodeBase64 = (data) => {
  return Buffer.from(data, "base64").toString("ascii");
};

/******************* 
@purpose : Used for content type
@Parameter : {} 
@Author : IndiaNIC
******************/
export const getHeader = (formData = false) => {
  return {
    "Content-Type": formData ? "multipart/form-data" : "application/json",
    Accept: "application/json",
  };
};

/******************* 
@purpose : Used for File upload extension
@Parameter : {} 
@Author : INIC
******************/
export const FILE_UPLOAD_EXTENSION = [
  "image/png", // for .png image
  "image/jpeg", // for .jpeg image
  "image/jpg", // for .jpg image
  // ".svg", // for .svg image
];

/******************* 
@purpose : Used for Remember Me Login
@Parameter : {} 
@Author : INIC
******************/

export const rememberMe = (clearTheme = true) => {
  const isRemembered = localStorage?.getItem(localStorageKeys.REMEMBER_ME);
  if (!isRemembered) {
    localStorage.clear();
  } else {
    if (clearTheme) {
      localStorage.removeItem(localStorageKeys.THEME);
    }
    localStorage.removeItem(localStorageKeys.EXPIRATIONTIME);
    localStorage.removeItem(localStorageKeys.IS_EMAIL);
    localStorage.removeItem(localStorageKeys.IS_MOBILE);
    localStorage.removeItem(localStorageKeys.ma_la);
    localStorage.removeItem(localStorageKeys.MASKED_EMAIL);
    localStorage.removeItem(localStorageKeys.MASKED_PHONE);
    localStorage.removeItem(localStorageKeys.OTP_TOKEN);
    localStorage.removeItem(localStorageKeys?.VERIFY_LOGIN_TOKEN);
  }
};

/******************* 
@purpose : Used for show default profile image onerror
@Parameter : {} 
@Author : INIC
******************/

export const setDefaultProfilePic = (e) => {
  e.currentTarget.src = profilepic;
  e.currentTarget.onerror = null;
};

/******************* 
@purpose : Used for show default profile image onerror
@Parameter : {} 
@Author : INIC
******************/

export const setDefaultLogo = (e) => {
  e.currentTarget.src = logo;
  e.currentTarget.onerror = null;
};
export const setDefaultFavicon = (e) => {
  e.currentTarget.src = favicon;
  e.currentTarget.onerror = null;
};

export const arrayToJson = (array, key) => {
  let data = {};
  for (let obj of array) {
    data[obj[key]] = obj;
  }
  return data;
};

export const jsonToArray = (obj) => Object.values(obj);

/******************* 
@purpose : Used for formatting date 
@Parameter : {date, dateFormat} 
@Author : INIC
******************/
export const getFormattedDate = (date, dateFormat) => {
  if (date === "") {
    return false;
  }
  if (dateFormat) {
    return moment(date).format(dateFormat);
  } else {
    return moment(date).format(DEFAULT_DATE_FORMAT);
  }
};

/******************* 
@purpose : Used for formatting date in date picker date format prop
@Parameter : {date, dateFormat} 
@Author : INIC
******************/
export const getFormattedDateWithTime = (dateFormat, timeFormat) => {
  if (timeFormat === "12 hours") {
    return `${dateFormat} h:mm aa`;
  } else if (timeFormat === "24 hours") {
    return `${dateFormat} HH:mm`;
  } else {
    return `${dateFormat} h:mm aa`;
  }
};

/******************* 
@purpose : Used for converting date according to timezone
@Parameter : {date, timezone} 
@Author : INIC
******************/
export const getTimeZoneConvertedDate = (date, timezone) => {
  if (date === "") {
    return false;
  }
  return moment.tz(date, timezone);
};

/******************* 
@purpose : Used for converting date to utc
@Parameter : {date} 
@Author : INIC
******************/
export const getUTCFormat = (date) => {
  return moment(date).utc().format("YYYY-MM-DDTHH:mm:ss");
};

export const getFormattedTimeWithTimezone = (time, timezone, timeFormat) => {
  if (timeFormat === "12 hours") {
    return moment(time).tz(timezone).format("hh:mm A");
  } else if (timeFormat === "24 hours") {
    return moment(time).tz(timezone).format("HH:mm");
  } else {
    return moment(time).tz(timezone).format("hh:mm A");
  }
};
export const getMomentFormattedDate = (data) => {
  return moment(data).format("MM-DD-YYYY- hh:mm A");
};

export const getFormattedDateTimeWithTimezone = (
  date,
  timezone,
  dateFormat = "YYYY/MM/DD",
  timeFormat = "24 hours",
  showTime = true
) => {
  if (!date) return "";

  let validTimezone = timezone?.includes(")") ? timezone.split(") ")[1] : timezone;
  validTimezone = validTimezone || "UTC";

  let momentDate;

  // Detect if input is in MM/DD/YYYY (like 03/31/2025)
  const isMDYFormat = /^\d{2}\/\d{2}\/\d{4}$/.test(date);
  const isYMDFormat = /^\d{4}-\d{2}-\d{2}$/.test(date);

  if (isYMDFormat) {
    momentDate = moment.utc(date, "YYYY-MM-DD").startOf("day").tz(validTimezone);
  } else if (isMDYFormat) {
    momentDate = moment.tz(date, "MM/DD/YYYY", true, validTimezone);
  } else {
    momentDate = moment(date).tz(validTimezone);
  }

  const formattedDate = momentDate.format(dateFormat);

  if (!showTime) return formattedDate;

  const formattedTime = timeFormat === "24 hours" ? momentDate.format("HH:mm") : momentDate.format("hh:mm A");

  return `${formattedDate} | ${formattedTime}`;
};

/******************* 
@purpose : Used for show alert popup 
@Parameter : {} 
@Author : INIC
******************/
export const fireDeletePopup = async (confirmDeleteHandler, resetGridRowSelection, title) => {
  const willDelete = await Swal.fire({
    title: title || "Are you sure, you want to delete?",
    icon: "warning",
    buttons: true,
    dangerMode: true,
    showCancelButton: true,
    confirmButtonText: "Delete",
    reverseButtons: true,
    focusConfirm: false,
    focusCancel: true,
  });

  if (willDelete.isConfirmed) {
    confirmDeleteHandler();
    if (resetGridRowSelection) resetGridRowSelection();
  }
};
/******************* 
@purpose : Used for show alert popup 
@Parameter : {} 
@Author : INIC
******************/
export const fireConformationPopup = async (confirmDeleteHandler, resetGridRowSelection, title, subtitle) => {
  const willDelete = await Swal.fire({
    title: title || "Are you sure, you want to delete?",
    html: subtitle ? `<p class="text-gray-500 mt-2">${subtitle}</p>` : "",
    icon: "warning",
    buttons: true,
    dangerMode: false,
    showCancelButton: true,
    confirmButtonText: "Yes",
    confirmButtonColor: "#1866FB", // Changed color to #1866FB
    reverseButtons: true,
    focusConfirm: false,
    focusCancel: true,
    customClass: {
      confirmButton: "swal-confirm-button",
      cancelButton: "swal-cancel-button",
    },
  });

  if (willDelete.isConfirmed) {
    confirmDeleteHandler();
    if (resetGridRowSelection) resetGridRowSelection();
  }
};
/******************* 
@purpose : Used for show alert popup 
@Parameter : {} 
@Author : INIC
******************/
export const suspendUserPopup = async (confirmDeleteHandler, resetGridRowSelection, title) => {
  const willDelete = await Swal.fire({
    title: title || "Are you sure, you want to suspend this user?",
    icon: "warning",
    buttons: true,
    dangerMode: true,
    showCancelButton: true,
    confirmButtonText: "Suspend",
    reverseButtons: true,
    focusConfirm: false,
    focusCancel: true,
  });

  if (willDelete.isConfirmed) {
    confirmDeleteHandler();
    if (resetGridRowSelection) resetGridRowSelection();
  }
};
export const cancelUserPopup = async (confirmDeleteHandler, resetGridRowSelection, title) => {
  const willDelete = await Swal.fire({
    title: title || "Are you sure, you want to cancelled this reported users?",
    icon: "warning",
    buttons: true,
    dangerMode: true,
    showCancelButton: true,
    confirmButtonText: "Cancelled",
    reverseButtons: true,
    focusConfirm: false,
    focusCancel: true,
  });

  if (willDelete.isConfirmed) {
    confirmDeleteHandler();
    if (resetGridRowSelection) resetGridRowSelection();
  }
};
export const revokeSuspensionPopup = async (confirmDeleteHandler, resetGridRowSelection, title) => {
  const willDelete = await Swal.fire({
    title: title || "Are you sure, you want to revoke this suspended user?",
    icon: "warning",
    buttons: true,
    dangerMode: true,
    showCancelButton: true,
    confirmButtonText: "Revoke",
    reverseButtons: true,
    focusConfirm: false,
    focusCancel: true,
  });

  if (willDelete.isConfirmed) {
    confirmDeleteHandler();
    if (resetGridRowSelection) resetGridRowSelection();
  }
};

export const getLanguageOptions = (data) => {
  return data?.map((list) => ({
    label: `${list?.language}(${list?.country})`,
    value: list?._id,
  }));
};

export const getLanguageCode = (data, id) => {
  return id && data ? data?.find((list) => list?._id === id)?.languageCode : DEFAULT_LANGUAGE_CODE;
};

/******************* 
@purpose : Used for setting default pic in blog card view 
@Parameter : {} 
@Author : INIC
******************/
export const setDefaultBlogImage = (e) => {
  e.currentTarget.src = blogimg;
  e.currentTarget.onerror = null;
};
/******************* 
@purpose : Used for select Option in react select 
@Parameter : {} 
@Author : INIC
******************/
export const selectOptionHandler = (formik, name) => {
  return formik?.values?.[name]
    ? {
        label: formik?.values?.[name],
        value: formik?.values?.[name],
      }
    : undefined;
};

/******************* 
@purpose : Used for conditional ternary 
@Parameter : {} 
@Author : INIC
******************/
export const conditionalTernaryHandler = (condition, param1, param2) => {
  return condition ? param1 : param2;
};

/******************* 
@purpose : Used for common ternary 
@Parameter : {} 
@Author : INIC
******************/
export const commonTernaryHandler = (param, param2) => {
  return param ? param : param2;
};

/******************* 
@purpose : Used for download file
@Parameter : {type,file} 
@Author : INIC
******************/
export const downlaodFiletoType = async (type, filepath) => {
  let file = `${apiConfig.FILE_DOWNLOAD_URL}/${type}/${filepath}`;
  await fetch(file)
    .then((res) => res.blob())
    .then((data) => {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filepath);
      link.click();
    });
};

/******************* 
@purpose : Used for sorting array of objects
@Parameter : {arr, key} 
@Author : INIC
******************/
export const sortArrOfObj = (arr, key) => {
  return arr.sort((a, b) => {
    if (a[key] < b[key]) {
      return -1;
    }
    if (a[key] > b[key]) {
      return 1;
    }
    return 0;
  });
};
/******************* 
@purpose : Used to renderred the Images of the current status 
@Parameter : 
@Author : INIC
******************/

export const formatUrlPathScopes = (pathname, params, permission) => {
  // Remove any params from URL
  let pathWithoutParams = pathname;
  Object.values(params).forEach((param) => {
    pathWithoutParams = pathWithoutParams.replace(new RegExp(`/${param}(?=/|$)`, "g"), "");
  });

  // Get CRUD scope from URL (i.e., view/create/edit)
  const pathScope = Object.values(permission).find((scope) =>
    pathWithoutParams.toLowerCase().includes(scope.toLowerCase())
  );

  // Get URL module path
  const pathWithoutScope = pathScope
    ? pathWithoutParams.replace(new RegExp(`/${pathScope.toLowerCase()}(?=/|$)`, "g"), "")
    : pathWithoutParams;

  return { pathWithoutParams, pathScope, pathWithoutScope };
};

/******************* 
@purpose : Used to renderred the role and permission 
@Parameter : 
@Author : INIC
******************/

export const rolePermissionData = (RolePermission, moduleCategory, role_permission) => {
  return RolePermission?.find((el) => el.category === moduleCategory)?.permissions.find(
    (permission) => permission.permission === role_permission
  )?.isSelected;
};
export const displayEtiology = (etiology) => {
  if (etiology?.etiologyName && etiology?.etiologyName !== "other") {
    return etiology?.etiologyName;
  } else if (etiology?.etiologyName === "other" && etiology?.etiologyOther) {
    return etiology?.etiologyOther;
  } else {
    return ""; // Or a default value, e.g., "N/A"
  }
};

export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toISOString().split("T")[0];
};

export const getTimeDifference = (dateString) => {
  const givenTime = moment(dateString); // Parse the given timestamp
  const currentTime = moment(); // Get the current time

  const diffInHours = currentTime.diff(givenTime, "hours");
  const diffInMinutes = currentTime.diff(givenTime, "minutes");

  if (diffInHours >= 1) {
    return `${diffInHours} h`;
  } else {
    return `${diffInMinutes} min`;
  }
};

export const organizeNotifications = (notifications) => {
  const groups = {};
  const groupOrder = [];

  notifications?.forEach((notification) => {
    const notifDate = moment.utc(notification.createdAt);
    let groupKey;

    if (notifDate.isSame(moment(), "day")) {
      groupKey = "Today";
    } else if (notifDate.isSame(moment().subtract(1, "day"), "day")) {
      groupKey = "Yesterday";
    } else if (notifDate.isAfter(moment().subtract(7, "days"), "day")) {
      groupKey = notifDate.format("dddd"); // Monday, Tuesday...
    } else {
      groupKey = notifDate.format("MM/DD/YYYY"); // Older than a week
    }

    if (!groups[groupKey]) {
      groups[groupKey] = [];
      groupOrder.push({
        key: groupKey,
        timestamp: notifDate.valueOf(),
      });
    }
    groups[groupKey].push(notification);
  });

  // Sort groups (Today, Yesterday first, then week-wise, then older)
  groupOrder?.sort((a, b) => {
    if (a.key === "Today") return -1;
    if (b.key === "Today") return 1;
    if (a.key === "Yesterday") return -1;
    if (b.key === "Yesterday") return 1;
    return b.timestamp - a.timestamp; // Newest date first
  });

  // Return structured data with sorted notifications inside each group
  return groupOrder?.map((group) => ({
    [group.key]: groups?.[group.key]?.sort(
      (a, b) => moment.utc(b.createdAt).valueOf() - moment.utc(a.createdAt).valueOf()
    ),
  }));
};

export const organizeNotificationsTime = (notifications, globalConfigData) => {
  const groups = {};
  const groupOrder = [];

  notifications?.forEach((notification) => {
    const notifDate = moment.utc(notification.createdAt);
    let groupKey;

    if (notifDate.isSame(moment(), "day")) {
      groupKey = "Today";
    } else if (notifDate.isSame(moment().subtract(1, "day"), "day")) {
      groupKey = "Yesterday";
    } else if (notifDate.isAfter(moment().subtract(7, "days"), "day")) {
      groupKey = notifDate.format("dddd"); // Monday, Tuesday...
    } else {
      // Use the date format from globalConfigData
      const dateFormat = globalConfigData?.data?.dateFormat || "MM/DD/YYYY";
      groupKey = notifDate.format(dateFormat); // Older than a week
    }

    if (!groups[groupKey]) {
      groups[groupKey] = [];
      groupOrder.push({
        key: groupKey,
        timestamp: notifDate.valueOf(),
      });
    }
    groups[groupKey].push(notification);
  });

  // Sort groups (Today, Yesterday first, then week-wise, then older)
  groupOrder?.sort((a, b) => {
    if (a.key === "Today") return -1;
    if (b.key === "Today") return 1;
    if (a.key === "Yesterday") return -1;
    if (b.key === "Yesterday") return 1;
    return b.timestamp - a.timestamp; // Newest date first
  });

  // Return structured data with sorted notifications inside each group
  return groupOrder?.map((group) => ({
    [group.key]: groups?.[group.key]?.sort(
      (a, b) => moment.utc(b.createdAt).valueOf() - moment.utc(a.createdAt).valueOf()
    ),
  }));
};
export const formatDateTime = (timestamp, globalConfigData) => {
  try {
    // Extract timezone
    const timeZone = globalConfigData?.data?.timeZone?.split(") ")[1] || "UTC";

    // Extract time format and determine if it's 12-hour format
    const timeFormatString = globalConfigData?.data?.timeFormat || "24 hours";
    const is12Hour = timeFormatString.includes("12");

    // Parse the timestamp
    const dateObj = new Date(timestamp);

    // Validate the date
    if (isNaN(dateObj.getTime())) {
      throw new Error("Invalid date");
    }

    // Configure formatting options
    const options = {
      timeZone: timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: is12Hour,
    };

    return new Intl.DateTimeFormat("en-US", options).format(dateObj);
  } catch (error) {
    return "Invalid Time";
  }
};
/** ***************** 
    @purpose : validate decimal input
    @Parameter : (value)
    @Author : INIC
    ***************** */
export const validateDecimalInput = (value) => {
  // Allow empty string for initial state
  if (value === "") return true;

  // Check if input matches the pattern of decimal numbers with up to 2 decimal places
  const regex = /^\d*\.?\d{0,2}$/;
  return regex.test(value);
};
